import { AppShell, Burger, Divider, Group, Image, Text } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import {
  IconChartColumn,
  IconCreditCard,
  IconHelpCircle,
  IconHexagons,
  IconSettings,
  IconThumbUp,
  IconUsers,
} from '@tabler/icons-react'
// import { autorun } from 'mobx'
import { observer } from 'mobx-react-lite'
import type { PropsWithChildren } from 'react'
import { useMemo } from 'react'

// import { Link } from 'wouter'
import logoImage from '@/assets/images/Logo-light-punchout.png'
import { AccountMenu } from '@/components/AccountMenu'
import { BeforeYouStartPopup } from '@/components/BeforeYouStartPopup'
import { BulkUploadHost } from '@/components/BulkUploadHost'
import { CardExpiryBanner } from '@/components/CardExpiryBanner'
import { GlobalLoadingOverlay } from '@/components/GlobalLoadingOverlay'
import { Navbar } from '@/components/Navbar'
import type { LinksGroup } from '@/components/NavbarLinksGroup'
import { NavbarLinksGroup } from '@/components/NavbarLinksGroup'
import { SearchModal } from '@/components/SearchModal'
import { SetupChecklist } from '@/components/SetupChecklist'
import { SidekickDeeplinkIcon } from '@/components/SidekickDeeplinkIcon'
import { SkipToContent } from '@/components/SkipToContent'
import { theme } from '@/configs/theme'
import { AllEsksProvider } from '@/hooks/useAllEsks'
import { useBillingDisabledStatus } from '@/hooks/useBillingDisabledStatus'
import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import * as skipClasses from '@/styles/SkipToContent.css'
import { getChannelDeeplinkUrl } from '@/utils/sponsors'

type DashboardLayoutProps = PropsWithChildren

const MAIN_CONTENT_ID = 'main-content'

export const DashboardLayout = observer(function DashboardLayout(props: DashboardLayoutProps) {
  const { authenticationStore, configStore } = useGlobalState()
  const { user, isSponsorAdmin } = authenticationStore
  const sponsorName = user?.sponsorName
  const isMobile = useCheckMobileScreen()
  const { isBillingRestricted } = useBillingDisabledStatus()

  const role = isSponsorAdmin ? 'Program Manager' : 'Expert'
  const channelDeeplinkUrl = getChannelDeeplinkUrl(configStore.authBaseUrl, user?.sponsorId)

  const [mobileDrawerOpened, { toggle: toggleMobileDrawer }] = useDisclosure()
  const mdBreakpoint = useMediaQuery(`(min-width: ${theme.breakpoints.md})`)

  const links = useMemo(() => {
    const staticLinks: LinksGroup[] = [
      // Disabled: https://infinitered.slack.com/archives/C07MY1RQ63S/p1731088788357509
      // { label: 'Dashboard', icon: IconTable, to: { name: 'dashboard' } },
      // { label: 'Getting Started', icon: IconRocket, to: { name: 'gettinStarted' } },

      {
        label: 'Content Library',
        icon: IconHexagons,
        to: { name: 'contentModules' },
        additionalCurrentRoutes: ['contentModuleFormNew', 'contentModuleFormEdit', 'contentModule'],
      },
      { label: 'People', icon: IconUsers, to: { name: 'people' } },
      { label: 'Feedback', icon: IconThumbUp, to: { name: 'feedback' } },
      !isMobile
        ? {
            label: 'Metrics',
            icon: IconChartColumn,
            to: { name: 'metrics' },
          }
        : null,
      isSponsorAdmin
        ? { label: 'Billing', icon: IconCreditCard, to: { name: 'billing' }, warning: isBillingRestricted }
        : null,
      isSponsorAdmin ? { label: 'Settings', icon: IconSettings, to: { name: 'settings' } } : null,
      channelDeeplinkUrl ? { label: 'Web App', icon: SidekickDeeplinkIcon, href: channelDeeplinkUrl } : null,
      { label: 'Help', icon: IconHelpCircle, to: { name: 'help' } },
    ].filter(Boolean) as LinksGroup[]

    return staticLinks.map((item, index) => (
      <NavbarLinksGroup {...item} key={typeof item.label === 'string' ? item.label : index} />
    ))
  }, [isSponsorAdmin, isMobile, isBillingRestricted, channelDeeplinkUrl])

  // useEffect(() => {
  //   const disposer = autorun(() => {
  //     chatStore.submitMockSMESKChatFn?.(undefined, undefined, { typing: true, skipQueue: true })
  //     chatStore.submitMockSMESKChatFn?.(
  //       <Stack gap="xs">
  //         <Text size="sm">{`Hello! I'm your Sidekick and I'm here to help you.`}</Text>
  //         <Text size="sm">{`The knowledge base for your Expert Sidekicks is made up of modules that you create. Think of it like training a new staff member on everything they need to know to serve your population. On this page, you can edit current modules or create new modules by uploading your existing files or manually entering content.`}</Text>
  //         <Text size="sm">{`What would you like to do? You can:`}</Text>
  //       </Stack>,
  //       [
  //         <Button
  //           key="create-module"
  //           component={Link}
  //           to={getRoutePath('contentModuleFormNew')}
  //           size="xs"
  //           leftSection={<IconHexagons stroke={1.5} size={20} />}>
  //           Create a Module
  //         </Button>,
  //         <Button
  //           key="view-metrics"
  //           component={Link}
  //           to={getRoutePath('metrics')}
  //           size="xs"
  //           leftSection={<IconChartColumn stroke={1.5} size={20} />}>
  //           View Metrics
  //         </Button>,
  //         <Button
  //           key="help"
  //           component={Link}
  //           to={getRoutePath('help')}
  //           size="xs"
  //           leftSection={<IconHelpCircle stroke={1.5} size={20} />}>
  //           See FAQs
  //         </Button>,
  //       ],
  //     )
  //   })

  //   return disposer
  // }, [])

  return (
    <>
      <SkipToContent targetId={MAIN_CONTENT_ID} />

      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: 72,
          breakpoint: 'md',
          collapsed: { mobile: !mobileDrawerOpened, desktop: false },
        }}>
        <AppShell.Header>
          <Group h="100%" px="md" justify="space-between">
            <Group>
              <Burger
                opened={mobileDrawerOpened}
                onClick={toggleMobileDrawer}
                hiddenFrom="md"
                size="md"
                aria-label={mobileDrawerOpened ? 'Close navigation menu' : 'Open navigation menu'}
              />
              <Image src={logoImage} alt="Sidekick Platform" h={40} w="auto" fit="contain" visibleFrom="md" />
            </Group>

            <Group gap="md">
              {sponsorName && role && (
                <Group gap="md" visibleFrom="sm">
                  <Group gap="xs">
                    <Text size="sm">Channel:</Text>
                    <Text size="sm" fw={600}>
                      {sponsorName}
                    </Text>
                  </Group>

                  <Divider orientation="vertical" />

                  <Group gap="xs">
                    <Text size="sm">Role:</Text>
                    <Text size="sm" fw={600}>
                      {role}
                    </Text>
                  </Group>
                </Group>
              )}

              <AccountMenu />
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar
          bg="transparent"
          withBorder={false}
          onClick={e => {
            if (mdBreakpoint) return
            if (e.target === e.currentTarget) {
              toggleMobileDrawer()
            }
          }}>
          <Navbar>{links}</Navbar>
        </AppShell.Navbar>

        <AppShell.Main id={MAIN_CONTENT_ID} tabIndex={-1} className={skipClasses.target} bg="gray.1">
          <AllEsksProvider>
            <BulkUploadHost />
            <SetupChecklist />
            <CardExpiryBanner />
            {props.children}
          </AllEsksProvider>
        </AppShell.Main>
      </AppShell>

      <BeforeYouStartPopup />
      <GlobalLoadingOverlay />

      <SearchModal
      // YULIANTODO: switch to https://mantine.dev/x/spotlight/
      // This is the same thing, but with prebuilt UI for search, limiting results, etc...
      />
    </>
  )
})
